import { useAuthContext } from "providers/Auth";
import { FEATURES } from "types/landlord/partner";

/**
 * UI feature flag gating the merged "Leasing" analytics dashboard (PLT-578).
 *
 * ON  — one Leasing tab: CRM modules, then the BoomScreen ones.
 * OFF — the two tabs it replaced, "BoomScreen" and "BoomCRM", exactly as they
 *       behaved before PLT-578/PLT-580/PLT-634/PLT-680.
 *
 * This is a `ui_` registry flag (Boom::UiFeatureFlag), so the key arrives in
 * the partner's `features` array and is toggled per partner from admin's
 * /ui-feature-flags page — no client deploy to flip it, and it works locally
 * where PostHog has no token and every PostHog flag reads as undefined.
 *
 * A partner that has not loaded yet counts as OFF, so nobody is shown the new
 * page before their features are known.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * REMOVING THE OLD TABS
 *
 * Everything the old tabs need is reachable from this file. To retire them:
 *
 *  1. Delete `views/analytics/legacy/` (the whole folder).
 *  2. Delete this file, and in `views/analytics/index.tsx` drop the branch so
 *     it re-exports `LeasingAnalyticsView` directly.
 *  3. In `views/analytics/constants.ts`, drop `AnalyticsTab.APPLICATIONS` and
 *     `AnalyticsTab.CRM`.
 *  4. In `views/analytics/customDashboard/useCustomDashboards.ts`, drop the
 *     `fallbackTab` option and go back to `AnalyticsTab.LEASING`.
 *  5. In `views/analytics/slices/AnalyticsTable.tsx`, make `countingUnit` and
 *     `onCountingUnitChange` required again and drop the optional chaining.
 *  6. In `views/analytics/components/AddModuleDrawer.tsx`, drop the legacy
 *     CRM/Screening tab branch.
 *  7. In `shared/app/BoomCrmPaywall/`, drop the `analytics` paywall: its entry
 *     in `constants.ts`, its title in `PaywallScreen.tsx`, `ChartsPage` in
 *     `PageSkeleton.tsx`, and `previews/AnalyticsPreview.tsx`.
 *  8. In `views/properties-id-units-id/components/AnalyticsTab/`, drop the
 *     `leasingDashboardEnabled` branch that still renders the Lead pipeline
 *     card and modal, along with its `lead_pipeline` and `lead_sources`
 *     queries in `controller.ts`.
 *
 * The sites in steps 1–7 are marked with a `LEGACY ANALYTICS TABS` comment.
 */
export const useLeasingDashboardEnabled = () => {
  const { partner } = useAuthContext();

  return Boolean(partner?.features.includes(FEATURES.UI_LEASING_DASHBOARD));
};
